import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

export default [
  '$scope',
  'UserService',
  'SubscriptionService',
  '$filter',
  'FEATURES',
  'moment',
  SubscriptionController
];

/**
 * @constructor
 * @param {SubscriptionController$Scope} $scope
 * @param {import('core-services/user.service').UserService} UserService
 * @param {import('core-services/subscription.service').SubscriptionService} SubscriptionService
 * @param {ng.IFilterService} $filter
 * @param {import('./features').FeatureFlags} FEATURES
 * @param {moment} moment
 */
export function SubscriptionController(
  $scope,
  UserService,
  SubscriptionService,
  $filter,
  FEATURES,
  moment,
) {

  if ($scope.setControllerName)
    $scope.setControllerName('SubscriptionCtrl');

  $scope.hasSubscription = SubscriptionService.hasSubscription;

  $scope.FEATURES = FEATURES;

  $scope.loading = 0;
  $scope.isProcessing = false;
  $scope.message = null;
  $scope.errorMessage = null;
  $scope.subscription = null;

  $scope.manageSubscriptionWasClicked = function(event) {
    event.preventDefault();
    return SubscriptionService.fetchSubscriptionManagementStrategy()
      .then(async function(result) {
        await SubscriptionService.manageSubscription(result);
      });
  };

  /**
   * @param {object} args
   * @param {import('types/api').V1.UserFromAPI} args.user
   * @param {import('types/api').V1.SubscriptionPlan} args.plan
   */
  function updateSubscriptionMessaging({ user, plan }) {
    if(!SubscriptionService.hasSubscription) {
      // No subscription
      $scope.message = "Your Tiller Money subscription is " + $filter('currency')(new SubscriptionPlan(plan).amountAsNumber)
        + "/" + plan.interval + " after your " + plan.trialPeriodDays
        + "-day trial. Cancel anytime.";
      $scope.collapsedMessage = "Update your billing information.";
      return;
    }

    $scope.message = subscriptionStatusStr({user, plan});
    $scope.collapsedMessage = subscriptionStatusCollapsedStr(user);
  }

  function subscriptionStatusCollapsedStr(user) {
    if (!user.subscription) {
      return "Update your billing information.";
    } else if (user.state == 'trialing') {
      if (user.subscription.cancel_at_period_end) {
        return "Update your billing information.";
      } else {
        return "Your free trial subscription is active.";
      }
    } else if (user.state == 'active') {
      if (user.subscription.cancel_at_period_end) {
        return "Update your billing information.";
      } else {
        return "Your subscription is active."
      }
    } else if (user.state == 'canceled'){
      return "Update your billing information.";
    } else if (user.state == 'expired'){
      return "Update your billing information.";
    } else if (user.state == 'past_due') {
      return "Your subscription has expired, please update your billing information."
    } else {
      return "We are unable to determine the status of your subscription."
    }
  }

  /**
   * @param {object} args
   * @param {import('types/api').V1.UserFromAPI} args.user
   * @param {import('types/api').V1.SubscriptionPlan} args.plan
   */
  function subscriptionStatusStr({user, plan}) {
    var MONTH_DIFF = 6;

    if(user.state == 'canceled') {
      return "Your subscription has been canceled. If you change your mind, you can reactivate your subscription and we'll resume regular updates to your Tiller Sheets.";
    }

    /** @type {any} */
    var discount = user.subscription.discount;
    var amount = new SubscriptionPlan(plan).amountAsNumber;

    var amountStr = $filter('currency')(amount);
    var now = moment();
    var trialEndMoment = moment( Number(user.subscription.trial_end) * 1000);
    var trialEndStr = trialEndMoment.format("MMMM Do");
    if (trialEndMoment.diff(now, 'months') > MONTH_DIFF) {
      trialEndStr = trialEndMoment.format("MMMM Do, YYYY");
    }
    var nextBillingDateStr = trialEndStr;

    var subscriptionPlan = new SubscriptionPlan(plan);
    var interval = subscriptionPlan.interval;
    var trialPeriodDays = subscriptionPlan.trialPeriodDays;

    var currentPeriodEnd = moment( Number(user.subscription.current_period_end) * 1000);

    var currentPeriodEndStr = currentPeriodEnd.format("MMMM Do");
    if (currentPeriodEnd.diff(now, 'months') > MONTH_DIFF) {
      currentPeriodEndStr = currentPeriodEnd.format("MMMM Do, YYYY");
    }

    if (user.subscription.cancel_at_period_end) {
      var currentPeriodEndStr = currentPeriodEnd.format("MMMM Do");
      return "Subscription canceled, your Tiller Sheets will no longer be updated after " + currentPeriodEndStr + ".  If you change your mind, you can reactivate your subscription and we'll resume regular updates.";
    } else if (discount) {
      var discountIntervalVerb;
      var discountDuration;
      if (discount.coupon
        && discount.coupon.duration_in_months) {
        if (discount.coupon.duration_in_months > 1) {
          discountDuration = '' + discount.coupon.duration_in_months + ' months';
          discountIntervalVerb = "are";
        } else {
          discountDuration = 'month';
          discountIntervalVerb = "is";
        }
      } else if (discount.end) {
        discountDuration = moment.duration((discount.end - discount.start) * 1000).humanize();
        discountIntervalVerb = "are";
        if (discountDuration.indexOf('a ') == 0) {
          discountIntervalVerb = "is";
          discountDuration = discountDuration.substring(2);
        }
      }

      var discountDesc = "full price";

      if (discount.coupon) {
        if (discount.coupon.percent_off) {
          discountDesc = discount.coupon.percent_off + "% off";
          if (discount.coupon.percent_off == "100") {
            discountDesc = "free";
            if (discount.end > user.subscription.trial_end) {
              var discountEndMoment = moment(discount.end * 1000);
              nextBillingDateStr = discountEndMoment.format("MMMM Do");
              if (discountEndMoment.diff(now, 'months') > MONTH_DIFF) {
                nextBillingDateStr = discountEndMoment.format("MMMM Do, YYYY");
              }
            }
          }
        } else if (discount.coupon.amount_off) {
          var amountOff = new Number(discount.coupon.amount_off).valueOf() / 100
          amount -= amountOff;
          amountStr = $filter('currency')(amount);
          discountDesc = $filter('currency')(amountOff) + " off";
        }
      }


      if (discountDuration) {
        return "Your Tiller subscription is active and your first " + discountDuration + " " + discountIntervalVerb + " " + discountDesc + ". We'll charge your card " + amountStr + " at the start of your next " + interval + "ly billing cycle on " + nextBillingDateStr + ". Thank you!";
      } else { // discount with no end
        if (discountDesc == "free") {
          return "Your Tiller subscription is active and free.";
        } else {
          return "Your Tiller subscription is active and " + discountDesc + ". We'll charge your card " + amountStr + " at the start of your next " + interval + "ly billing cycle on " + nextBillingDateStr + ". Thank you!";
        }
      }

    } else if (user.state == 'trialing') {
      var daysLeft = trialEndMoment.diff(now, 'days');

      return "You have " + daysLeft + " days left of your free trial. Your credit card won't be charged until after the free trial ends. Thanks for subscribing!"
    } else if (user.state == 'expired') {
      return "Your previous free trial subscription has expired. If you change your mind, you can reactivate your subscription and we'll resume regular updates to your Tiller Sheets.";
    } else if (user.state == 'active') {
      return "Your subscription is active and paid. Thank you! We'll charge your card " + amountStr + " at the start of your next " + interval + "ly billing cycle on " + currentPeriodEndStr + ".";
    } else if (user.state == 'past_due') {
      return "We need you to renew your billing information.";
    } else {
      return "We are unable to determine the status of your subscription.";
    }
  }

  $scope.showUserProvidedSourceForm = function() {
    return ($scope.appData.user.state == 'setup');
  };

  $scope.needsToSAccepted = function() {
    if ($scope.appData.user.state == 'setup'
       || $scope.appData.user.state == 'canceled'
       || $scope.appData.user.state == 'expired') {
      return true;
    }

    return false;
  }

  // setup collapse state
  UserService.user$
    .pipe(
      filter(_ => !!_),
      take(1),
    ).subscribe(user => {
      if ((user.state != 'trialing' && user.state != 'active')
          || user.subscription.cancel_at_period_end) {
        $scope.expand()
      }
    });

  combineLatest(
    UserService.user$,
    SubscriptionService.plan$,
  ).subscribe(([user, plan]) => {
    $scope.hasSubscription = SubscriptionService.hasSubscription;

    updateSubscriptionMessaging({ user, plan });
  });
};

/**
 * @constructor
 * @param {SubscriptionController.Plan} plan
 */
function SubscriptionPlan(plan) {
  this.plan = plan;

  if (this.plan) {
    this.plan_id  = this.plan.plan_id;
    this.amount   = this.plan.amount;
    this.interval = this.plan.interval;
    this.name     = this.plan.name;
    this.trialPeriodDays = this.plan.trialPeriodDays;

    this.amountAsNumber = new Number(this.amount).valueOf() / 100;
  }
}


/**
 * @typedef {Pick<
 *    import('types/api').V1.SubscriptionPlan,
 *    'plan_id'
 *    | 'interval'
 *    | 'amount'
 *    | 'name'
 *    | 'trialPeriodDays'
 * >} SubscriptionController.Plan
 */

/**
 * @typedef {ng.IScope & import('./collapsible.controller').IScope & SubscriptionController.MyScope} SubscriptionController$Scope
 */
/**
 * @typedef {object} SubscriptionController.MyScope
 * @property {import('./tiller-app.controller').TillerAppController$Scope['appData']} appData
 * @property {import('./features').FeatureFlags} FEATURES
 * @property {boolean} hasSubscription
 * @property {number} loading
 * @property {boolean} isProcessing
 * @property {string} message
 * @property {string} collapsedMessage
 * @property {string} errorMessage
 * @property {import('types/api').V1.UserSubscription} subscription
 *
 * @property {() => void} manageSubscriptionWasClicked
 * @property {() => void} handleSubscriptionChanged
 * @property {() => boolean} showUserProvidedSourceForm
 * @property {() => boolean} needsToSAccepted
 */
